import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import StepActions from './StepActions';
import { isFixedPriceService, isQuoteService } from '../servicePricing';

const PictureStep = ({ data, onUpdate, onNext, onBack, isActive = true, showBack = true }) => {
  const [description, setDescription] = useState(data?.description || '');
  const [photos, setPhotos] = useState(data?.photos || []);
  const [errors, setErrors] = useState({});
  const [isDragging, setIsDragging] = useState(false);

  const maxPhotos = 5;

  const handleFileSelect = (files) => {
    const fileArray = Array.from(files);
    const remainingSlots = maxPhotos - photos?.length;
    const filesToAdd = fileArray?.slice(0, remainingSlots);

    const newPhotos = filesToAdd?.map((file, index) => ({
      id: Date.now() + index,
      file,
      preview: URL.createObjectURL(file),
      isOverview: photos?.length === 0 && index === 0,
    }));

    setPhotos((prev) => [...prev, ...newPhotos]);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    const files = e?.dataTransfer?.files;
    handleFileSelect(files);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleFileInput = (e) => {
    const files = e?.target?.files;
    handleFileSelect(files);
  };

  const removePhoto = (photoId) => {
    setPhotos((prev) => {
      const filtered = prev?.filter((p) => p?.id !== photoId);
      if (filtered?.length > 0 && !filtered?.some((p) => p?.isOverview)) {
        filtered[0].isOverview = true;
      }
      return filtered;
    });
  };

  const setAsOverview = (photoId) => {
    setPhotos((prev) =>
      prev?.map((p) => ({
        ...p,
        isOverview: p?.id === photoId,
      }))
    );
  };

  const handleSubmit = () => {
    const newErrors = {};

    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    onUpdate({
      description,
      photos,
    });

    // Devis : priorité/créneau sont déjà retirés du parcours → étape suivante = Coordonnées
    if (isQuoteService(data?.service)) {
      onNext(true);
      return;
    }

    // Autre « non prix fixe » avec étapes priorité/créneau présentes : les sauter
    if (!isFixedPriceService(data?.service)) {
      onNext(true, { skipToStep: 7 });
      return;
    }

    onNext(true);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-card rounded-lg shadow-md p-6 lg:p-8">
        <p className="text-muted-foreground mb-4">
          Vous pouvez ajouter un commentaire (facultatif)  et joindre jusqu’à 5 photos. Cela nous aide à préparer l’intervention.
        </p>

        <div className="space-y-4">
          <div>
            <textarea
              placeholder="Commentaire (facultatif)"
              className={`w-full px-4 py-3 border rounded-lg bg-card text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                errors?.description ? 'border-error' : 'border-border'
              }`}
              value={description}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= 250) {
                  setDescription(value);
                  if (errors?.description) setErrors((prev) => ({ ...prev, description: '' }));
                }
              }}
              rows={2}
              maxLength={250}
            />
            <p className={`text-xs text-right mt-1 ${
              description.length >= 250 ? 'text-error' : 'text-muted-foreground'
            }`}>
              {description.length}/250
            </p>
          </div>

          <div>
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                isDragging
                  ? 'border-primary bg-primary/5'
                  : photos?.length >= maxPhotos
                  ? 'border-muted bg-muted/20'
                  : 'border-border hover:border-primary/50'
              }`}
            >
              {photos?.length < maxPhotos ? (
                <>
                  <p className="text-sm text-muted-foreground mb-3">
                    Photos {photos?.length}/{maxPhotos}
                  </p>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileInput}
                    className="hidden"
                    id="photo-upload"
                  />
                  <label htmlFor="photo-upload">
                    <Button variant="outline" size="sm" iconName="Plus" asChild>
                      <span>Ajouter une photo</span>
                    </Button>
                  </label>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Limite de {maxPhotos} photos atteinte
                </p>
              )}
            </div>

            {photos?.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-3">
                {photos?.map((photo) => (
                  <div key={photo?.id} className="relative group">
                    <div className="aspect-square rounded-lg overflow-hidden border border-border">
                      <Image
                        src={photo?.preview}
                        alt={`Photo ${photo?.isOverview ? "(vue d'ensemble)" : ''}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {photo?.isOverview && (
                      <div className="absolute top-1 left-1 bg-primary text-primary-foreground text-[10px] font-medium px-1.5 py-0.5 rounded">
                        Vue d'ensemble
                      </div>
                    )}
                    <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {!photo?.isOverview && (
                        <button
                          onClick={() => setAsOverview(photo?.id)}
                          className="w-7 h-7 bg-card rounded-full flex items-center justify-center shadow hover:bg-primary hover:text-primary-foreground transition-colors"
                          title="Définir comme vue d'ensemble"
                          type="button"
                        >
                          <Icon name="Star" size={14} />
                        </button>
                      )}
                      <button
                        onClick={() => removePhoto(photo?.id)}
                        className="w-7 h-7 bg-card rounded-full flex items-center justify-center shadow hover:bg-error hover:text-error-foreground transition-colors"
                        title="Supprimer"
                        type="button"
                      >
                        <Icon name="Trash2" size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <StepActions isActive={isActive} showBack={showBack} className="flex justify-between pt-6 mt-6 border-t border-border">
          <Button variant="outline" size="lg" iconName="ArrowLeft" onClick={onBack}>
            Retour
          </Button>
          <Button
            variant="default"
            size="lg"
            iconName="ArrowRight"
            iconPosition="right"
            onClick={handleSubmit}
          >
            Continuer
          </Button>
        </StepActions>
      </div>
    </div>
  );
};

export default PictureStep;
